import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2, ExternalLink, GripVertical } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";

interface ServiceOption {
  id: string;
  name: string;
  price: number;
  duration: number;
  isNew?: boolean;
}

interface AvailableUpsell {
  id: string;
  name: string;
  required: boolean;
  option_selection_mode: string;
  upsell_options: { id: string; name: string; price: number; duration: number }[];
}

interface LinkedUpsell {
  upsell_id: string;
  sort_order: number;
}

const ServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const isEditing = !!id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("");
  const [active, setActive] = useState(true);
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [optionSelectionMode, setOptionSelectionMode] = useState<"single" | "multiple">("multiple");
  const [optionRequired, setOptionRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [availableUpsells, setAvailableUpsells] = useState<AvailableUpsell[]>([]);
  const [linkedUpsells, setLinkedUpsells] = useState<LinkedUpsell[]>([]);

  // Fetch available upsells for this org
  useEffect(() => {
    const fetchUpsells = async () => {
      if (!profile?.organisation_id) return;
      const { data } = await supabase
        .from("upsells")
        .select("*, upsell_options(*)")
        .eq("organisation_id", profile.organisation_id)
        .order("name");
      setAvailableUpsells((data as any) || []);
    };
    fetchUpsells();
  }, [profile?.organisation_id]);

  // Fetch service data + linked upsells when editing
  useEffect(() => {
    if (!isEditing) return;
    const fetch = async () => {
      const [serviceRes, upsellsRes] = await Promise.all([
        supabase.from("services").select("*, service_options(*)").eq("id", id).maybeSingle(),
        supabase.from("service_upsells").select("upsell_id, sort_order").eq("service_id", id!).order("sort_order"),
      ]);
      if (serviceRes.data) {
        const data = serviceRes.data;
        setName(data.name);
        setDescription(data.description || "");
        setPrice(String(data.price));
        setDuration(String(data.duration));
        setActive(data.active);
        setOptionSelectionMode((data.option_selection_mode as any) || "multiple");
        setOptionRequired(data.option_required || false);
        setOptions(
          ((data as any).service_options || []).map((o: any) => ({
            id: o.id,
            name: o.name,
            price: Number(o.price),
            duration: o.duration,
          }))
        );
      }
      if (upsellsRes.data) {
        setLinkedUpsells(upsellsRes.data.map((su: any) => ({ upsell_id: su.upsell_id, sort_order: su.sort_order })));
      }
      setFetching(false);
    };
    fetch();
  }, [id, isEditing]);

  const addOption = () => {
    setOptions([...options, { id: crypto.randomUUID(), name: "", price: 0, duration: 0, isNew: true }]);
  };

  const updateOption = (optId: string, field: keyof ServiceOption, value: string | number) => {
    setOptions(options.map((o) => (o.id === optId ? { ...o, [field]: value } : o)));
  };

  const removeOption = (optId: string) => {
    setOptions(options.filter((o) => o.id !== optId));
  };

  const handleSave = async () => {
    if (!name || !price || !duration || !profile?.organisation_id) {
      toast.error(t("services.fill_required"));
      return;
    }
    setLoading(true);
    try {
      let serviceId = id;
      if (isEditing) {
        const { error } = await supabase
          .from("services")
          .update({
            name,
            description: description || null,
            price: parseFloat(price),
            duration: parseInt(duration),
            active,
            option_selection_mode: optionSelectionMode,
            option_required: optionRequired,
          })
          .eq("id", id!);
        if (error) throw error;

        // Delete existing options and re-insert
        await supabase.from("service_options").delete().eq("service_id", id!);
      } else {
        const { data, error } = await supabase
          .from("services")
          .insert({
            organisation_id: profile.organisation_id,
            name,
            description: description || null,
            price: parseFloat(price),
            duration: parseInt(duration),
            active,
            option_selection_mode: optionSelectionMode,
            option_required: optionRequired,
          })
          .select()
          .single();
        if (error) throw error;
        serviceId = data.id;
      }

      // Insert options
      if (options.length > 0 && serviceId) {
        const { error } = await supabase.from("service_options").insert(
          options.map((o) => ({
            service_id: serviceId!,
            name: o.name,
            price: o.price,
            duration: o.duration,
          }))
        );
        if (error) throw error;
      }

      // Save linked upsells
      if (serviceId) {
        await supabase.from("service_upsells").delete().eq("service_id", serviceId);
        if (linkedUpsells.length > 0) {
          const { error } = await supabase.from("service_upsells").insert(
            linkedUpsells.map((lu, i) => ({
              service_id: serviceId!,
              upsell_id: lu.upsell_id,
              sort_order: i,
            }))
          );
          if (error) throw error;
        }
      }

      toast.success(isEditing ? t("services.updated") : t("services.created"));
      navigate("/dashboard/services");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/services">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("services.back")}
        </Link>
      </Button>

      <h1 className="text-xl font-bold text-foreground mb-6">
        {isEditing ? t("services.edit_title") : t("services.create_title")}
      </h1>

      {/* Basic Details */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("services.details")}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label>{t("services.name_label")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("services.name_placeholder")} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>{t("services.description_label")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("services.description_placeholder")} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>{t("services.price_label")}</Label>
            <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
          </div>
          <div className="space-y-2">
            <Label>{t("services.duration_label")}</Label>
            <Input type="number" min="5" step="5" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="30" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Switch checked={active} onCheckedChange={setActive} />
          <Label className="cursor-pointer" onClick={() => setActive(!active)}>{t("services.active_label")}</Label>
          <span className="text-xs text-muted-foreground">
            {active ? t("services.active_desc") : t("services.inactive_desc")}
          </span>
        </div>
      </section>

      {/* Options */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t("services.options_title")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("services.options_desc")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={addOption}>
            <Plus className="h-4 w-4 mr-1" />
            {t("services.add_option")}
          </Button>
        </div>

        {options.length > 0 && (
          <div className="rounded-xl border border-border p-4 bg-muted/30 mb-4">
            <h3 className="text-sm font-medium text-foreground mb-3">{t("services.selection_settings")}</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("services.selection_mode")}</Label>
                <Select value={optionSelectionMode} onValueChange={(v: "single" | "multiple") => setOptionSelectionMode(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">{t("services.mode_single")}</SelectItem>
                    <SelectItem value="multiple">{t("services.mode_multiple")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 md:pt-5">
                <Switch checked={optionRequired} onCheckedChange={setOptionRequired} />
                <div>
                  <Label className="cursor-pointer text-sm" onClick={() => setOptionRequired(!optionRequired)}>{t("services.required")}</Label>
                  <p className="text-[11px] text-muted-foreground leading-tight">
                    {optionRequired ? t("services.required_desc") : t("services.optional_desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {options.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center">
            <p className="text-sm text-muted-foreground">{t("services.no_options")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {options.map((opt, idx) => (
              <div key={opt.id} className="rounded-xl border border-border p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground">{t("services.option_label")} {idx + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeOption(opt.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("services.option_name")}</Label>
                    <Input value={opt.name} onChange={(e) => updateOption(opt.id, "name", e.target.value)} placeholder={t("services.option_name_placeholder")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("services.option_price")}</Label>
                    <Input type="number" min="0" step="0.01" value={opt.price || ""} onChange={(e) => updateOption(opt.id, "price", parseFloat(e.target.value) || 0)} placeholder="0.00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("services.option_duration")}</Label>
                    <Input type="number" min="0" step="5" value={opt.duration || ""} onChange={(e) => updateOption(opt.id, "duration", parseInt(e.target.value) || 0)} placeholder="0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Upsell Steps */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t("services.upsells_title")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("services.upsells_desc")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard/upsells"><ExternalLink className="h-3.5 w-3.5 mr-1" />{t("services.manage_upsells")}</Link>
            </Button>
          </div>
        </div>

        {availableUpsells.length > 0 && (
          <div className="mb-4">
            <Select
              value=""
              onValueChange={(upsellId) => {
                if (linkedUpsells.some((lu) => lu.upsell_id === upsellId)) {
                  toast.error(t("services.upsell_already_added"));
                  return;
                }
                setLinkedUpsells([...linkedUpsells, { upsell_id: upsellId, sort_order: linkedUpsells.length }]);
              }}
            >
              <SelectTrigger className="w-full sm:w-80"><SelectValue placeholder={t("services.select_upsell")} /></SelectTrigger>
              <SelectContent>
                {availableUpsells
                  .filter((u) => !linkedUpsells.some((lu) => lu.upsell_id === u.id))
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.upsell_options.length} options)</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {linkedUpsells.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center">
            <p className="text-sm text-muted-foreground">{t("services.no_upsells")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {linkedUpsells.map((lu, idx) => {
              const upsell = availableUpsells.find((u) => u.id === lu.upsell_id);
              if (!upsell) return null;
              return (
                <div key={lu.upsell_id} className="rounded-xl border border-border p-4 bg-muted/30 flex items-start gap-3">
                  <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{upsell.name}</span>
                      <Badge variant="outline" className="text-[10px]">{upsell.required ? t("upsells.required") : t("upsells.optional_desc")}</Badge>
                      <Badge variant="outline" className="text-[10px]">{upsell.option_selection_mode === "single" ? t("upsells.single") : t("upsells.multiple")}</Badge>
                    </div>
                    {upsell.upsell_options.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {upsell.upsell_options.map((opt) => (
                          <Badge key={opt.id} variant="secondary" className="text-[10px] font-normal">
                            {opt.name} {opt.price > 0 && `+€${Number(opt.price).toFixed(2)}`} {opt.duration > 0 && `+${opt.duration}min`}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => setLinkedUpsells(linkedUpsells.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={!name || !price || !duration || loading}>
          {loading ? t("services.saving") : isEditing ? t("services.save_changes") : t("services.create")}
        </Button>
        <Button variant="ghost" asChild>
          <Link to="/dashboard/services">{t("services.cancel")}</Link>
        </Button>
      </div>
    </div>
  );
};

export default ServiceForm;